<div [formGroup]="form">
  <h3 class="textTabsColor mb-4 text-sm font-bold" i18n="@@stock.section.info">Stock Information</h3>

  <div class="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
    @if (!productId() && type() === "create") {
      <div class="relative flex flex-col gap-1.5">
        <label class="text-sm font-semibold text-gray-700">
          <span i18n="@@stock.field.product">Product</span>
          <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          formControlName="productSearch"
          (input)="onSearch($event)"
          [placeholder]="isArabic() ? 'ابحث عن صنف...' : 'Search for a product...'"
          class="focus:borderTabsColor focus-ring-primary-10 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm transition outline-none focus:ring-2"
          [class.border-red-500]="invalid('productSearch')" />

        @if (showAutocomplete() && productsAutocomplete().length > 0) {
          <div
            class="absolute z-50 mt-[72px] max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
            @for (prod of productsAutocomplete(); track prod.id) {
              <div
                (click)="select(prod)"
                class="hover-bg-primary-10 cursor-pointer border-b border-gray-50 px-4 py-2.5 text-sm last:border-0">
                {{ isArabic() ? prod.titleAr || prod.titleEn : prod.titleEn || prod.titleAr }}
              </div>
            }
          </div>
        }
        @if (err("productId", "required")) {
          <span class="text-xs text-red-500" i18n="@@stock.validation.productRequired">Please select a product</span>
        }
      </div>
    }

    @if (productId()) {
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-semibold text-gray-700" i18n="@@stock.field.product">Product</label>
        <div class="w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-gray-600">
          {{ form.get("productSearch")?.value }}
        </div>
      </div>
    }

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@product.field.zone">Zone</span>
        <span class="text-red-500">*</span>
      </label>
      <div
        class="w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-600"
        [class.cursor-not-allowed]="!form.get('zone')?.value">
        @if (form.get("zone")?.value) {
          <span class="font-medium text-gray-800">
            {{ form.get("zone")?.value }}
          </span>
        } @else if (productId() || form.get("productSearch")?.value) {
          <span class="text-gray-400">
            {{ isArabic() ? "لا يوجد زون" : "No Zone" }}
          </span>
        } @else {
          <span>&nbsp;</span>
        }
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@stock.field.supplier">Supplier</span>
        <span class="text-red-500">*</span>
      </label>
      <select
        formControlName="supplierId"
        class="focus:borderTabsColor focus-ring-primary-10 w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm transition outline-none focus:ring-2"
        [class.border-red-500]="invalid('supplierId')">
        <option [value]="null" disabled i18n="@@stock.placeholder.selectSupplier">Select Supplier</option>
        @for (s of suppliers(); track s.id) {
          <option [value]="s.id">{{ isArabic() ? s.titleAr : s.titleEn }}</option>
        }
      </select>
      @if (err("supplierId", "required")) {
        <span class="text-xs text-red-500" i18n="@@stock.validation.supplierRequired">Supplier is required</span>
      }
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@stock.field.quantity">Quantity</span>
        <span class="text-red-500">*</span>
      </label>
      <input
        type="number"
        formControlName="quantity"
        class="focus:borderTabsColor focus-ring-primary-10 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm transition outline-none focus:ring-2"
        [class.border-red-500]="invalid('quantity')" />
      @if (err("quantity", "required")) {
        <span class="text-xs text-red-500" i18n="@@stock.validation.qtyRequired">Quantity is required</span>
      } @else if (err("quantity", "min")) {
        <span class="text-xs text-red-500" i18n="@@stock.validation.qtyMin">Quantity must be at least 1</span>
      } @else if (err("quantity", "notInteger")) {
        <span class="text-xs text-red-500">
          {{
            isArabic()
              ? "يجب أن تكون الكمية عددًا صحيحًا. القيم العشرية غير مسموح بها."
              : "Quantity must be a whole number. Decimal values are not allowed."
          }}
        </span>
      }
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@stock.field.batchNumber">Batch Number</span>
        <span class="text-red-500">*</span>
      </label>
      <input
        type="text"
        formControlName="batchNumber"
        placeholder="e.g. B-12345"
        class="focus:borderTabsColor focus-ring-primary-10 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm transition outline-none focus:ring-2"
        [class.border-red-500]="invalid('batchNumber')"
        [class.cursor-not-allowed]="type() === 'edit'"
        [class.bg-gray-100]="type() === 'edit'" />
      @if (err("batchNumber", "required")) {
        <span class="text-xs text-red-500" i18n="@@stock.validation.batchRequired">Batch number is required</span>
      }
      @if (err("batchNumber", "maxlength")) {
        <span class="text-xs text-red-500">
          {{ isArabic() ? "لا يمكن أن يتجاوز رقم الدفعة 50 حرفًا." : "Batch Number cannot exceed 50 characters." }}
        </span>
      }
      @if (err("batchNumber", "pattern")) {
        <span class="text-xs text-red-500">
          {{
            isArabic()
              ? "رقم الدفعة يمكن أن يحتوي على حروف وأرقام وشرطات فقط."
              : "Batch Number can contain letters, numbers, and hyphens only."
          }}
        </span>
      }
      @if (err("batchNumber", "duplicate")) {
        <span class="text-xs text-red-500">
          {{
            isArabic()
              ? "رقم الدفعة هذا مضاف بالفعل في القائمة المؤقتة."
              : "This batch number has already been added to the temporary list."
          }}
        </span>
      }
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@stock.field.mfgDate">MFG Date</span>
        <span class="text-red-500">*</span>
      </label>
      <input
        type="date"
        formControlName="mfgDate"
        class="focus:borderTabsColor focus-ring-primary-10 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2"
        [class.border-red-500]="invalid('mfgDate') || form.get('mfgDate')?.hasError('backendMsg')"
        [class.cursor-not-allowed]="type() === 'edit'"
        [class.bg-gray-100]="type() === 'edit'" />
      @if (err("mfgDate", "required")) {
        <span class="text-xs text-red-500">
          {{ isArabic() ? "تاريخ الإنتاج مطلوب" : "Manufacturing Date is required" }}
        </span>
      } @else if (err("mfgDate", "futureDate") || form.get("mfgDate")?.hasError("backendMsg")) {
        <span class="text-xs text-red-500">
          {{ isArabic() ? "تاريخ الإنتاج لا يمكن أن يكون في المستقبل" : "Manufacturing Date cannot be in the future" }}
        </span>
      } @else if (err("mfgDate", "afterExpDate")) {
        <span class="text-xs text-red-500">
          {{
            isArabic()
              ? "تاريخ الإنتاج يجب أن يكون قبل تاريخ الانتهاء"
              : "Manufacturing Date must be before Expiration Date"
          }}
        </span>
      }
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-semibold text-gray-700">
        <span i18n="@@stock.field.expDate">EXP Date</span>
        <span class="text-red-500">*</span>
      </label>
      <input
        type="date"
        formControlName="expDate"
        class="focus:borderTabsColor focus-ring-primary-10 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2"
        [class.border-red-500]="invalid('expDate')"
        [class.cursor-not-allowed]="type() === 'edit'"
        [class.bg-gray-100]="type() === 'edit'" />
      @if (err("expDate", "required")) {
        <span class="text-xs text-red-500">
          {{ isArabic() ? "تاريخ انتهاء الصلاحية مطلوب" : "Expiry Date is required" }}
        </span>
      } @else if (err("expDate", "notFuture")) {
        <span class="text-xs text-red-500">
          {{ isArabic() ? "تاريخ الانتهاء يجب أن يكون تاريخاً مستقبلياً" : "Expiration Date must be a future date" }}
        </span>
      } @else if (err("expDate", "beforeMfgDate")) {
        <span class="text-xs text-red-500">
          {{
            isArabic()
              ? "تاريخ الانتهاء يجب أن يكون بعد تاريخ الإنتاج"
              : "Expiration Date must be after Manufacturing Date"
          }}
        </span>
      }
    </div>

    @if (form.errors?.["backend"]) {
      <div class="mt-4 md:col-span-2">
        <div class="rounded-lg border border-red-200 bg-red-50 p-4">
          <p class="text-sm font-bold text-red-600">
            {{ isArabic() ? form.errors?.["backend"].messageAr : form.errors?.["backend"].messageEn }}
          </p>
        </div>
      </div>
    }
  </div>
</div>
